/**
 * Auth context: session bootstrap from stored tokens, login/register/logout,
 * and the RequireAuth route guard. Subscribes to the api client's
 * onUnauthorized channel so a failed token refresh anywhere lands on /login.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router-dom";

import { api, clearTokens, hasTokens, onUnauthorized } from "./api";
import type { User } from "./types";
import { Spinner } from "../components/Spinner";

type AuthStatus = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  user: User | null;
  status: AuthStatus;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Re-fetch /auth/me (e.g. after a profile change). */
  reload: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>(hasTokens() ? "loading" : "anonymous");

  // Bootstrap: if tokens survived a reload, validate them against /auth/me.
  useEffect(() => {
    if (!hasTokens()) return;
    const controller = new AbortController();
    api.auth
      .me(controller.signal)
      .then((me) => {
        setUser(me);
        setStatus("authenticated");
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        // Refresh already ran (and failed) inside the client for 401s.
        clearTokens();
        setUser(null);
        setStatus("anonymous");
        void error;
      });
    return () => controller.abort();
  }, []);

  // Any request whose refresh fails ends the session.
  useEffect(
    () =>
      onUnauthorized(() => {
        setUser(null);
        setStatus("anonymous");
      }),
    [],
  );

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.auth.login({ email, password });
    setUser(res.user);
    setStatus("authenticated");
  }, []);

  const register = useCallback(async (name: string, email: string, password: string) => {
    const res = await api.auth.register({ email, password, name });
    setUser(res.user);
    setStatus("authenticated");
  }, []);

  const logout = useCallback(async () => {
    await api.auth.logout();
    setUser(null);
    setStatus("anonymous");
  }, []);

  const reload = useCallback(async () => {
    const me = await api.auth.me();
    setUser(me);
    setStatus("authenticated");
  }, []);

  const value = useMemo(
    () => ({ user, status, login, register, logout, reload }),
    [user, status, login, register, logout, reload],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Route guard — spinner while bootstrapping, /login (with return path) when anonymous. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return (
      <div className="flex h-screen items-center justify-center bg-ink">
        <Spinner size="lg" />
      </div>
    );
  }
  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

/** Inverse guard for /login and /register — bounce authed users home. */
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  if (status === "authenticated") {
    return <Navigate to={from} replace />;
  }
  return <>{children}</>;
}
